import React, { useEffect, useRef, useState } from 'react';
import { useBookingContext } from '../BookingContext';
import { addHoursToTime } from '../../../utils/time';
import ButtonSelect from '../../shared/ButtonSelect';
import identityApi from '../../../clients/IdentityClient';

const ActivitiesSection: React.FC = () => {
  const { booking, updateBooking } = useBookingContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const lastLoadedLocationRef = useRef<string | null>(null);

  useEffect(() => {
    const loadActivities = async () => {
      if (!booking.location) {
        setError('');
        lastLoadedLocationRef.current = null;
        updateBooking({ activities: [], activityId: null, endTime: '' });
        return;
      }

      if (lastLoadedLocationRef.current === booking.location) {
        return;
      }

      lastLoadedLocationRef.current = booking.location;
      setIsLoading(true);
      setError('');
      try {
        const response = await identityApi.getActivities(booking.location);
        const nextActivities = response.activities ?? [];

        if (nextActivities.length === 0) {
          updateBooking({ activities: [], activityId: null, endTime: '' });
          return;
        }

        const hasCurrentActivity = nextActivities.some((activity) => activity.id === booking.activityId);
        if (hasCurrentActivity) {
          updateBooking({ activities: nextActivities });
          return;
        }

        const first = nextActivities[0];
        updateBooking({
          activities: nextActivities,
          activityId: first.id,
          endTime: booking.startTime ? addHoursToTime(booking.startTime, first.durationInHours) : ''
        });
      } catch {
        updateBooking({ activities: [], activityId: null, endTime: '' });
        setError('Failed to load activities');
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [booking.activityId, booking.location, booking.startTime, updateBooking]);

  const selected = booking.activities.find((activity) => activity.id === booking.activityId);
  return (
    <div>
      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}
      <ButtonSelect
        options={booking.activities.map((activity) => activity.id)}
        selected={selected?.id}
        disabled={isLoading || booking.activities.length === 0}
        buttonClassName="duration-section-option-btn"
        onChange={(activityId: string) => {
          const selectedActivity = booking.activities.find((activity) => activity.id === activityId);
          if (!selectedActivity) {
            return;
          }

          updateBooking({
            activityId: selectedActivity.id,
            endTime: booking.startTime ? addHoursToTime(booking.startTime, selectedActivity.durationInHours) : ''
          });
        }}
        title="Select activity:"
        renderLabel={(activityId) => {
          const activity = booking.activities.find((a) => a.id === activityId);
          if (!activity) {
            return '';
          }
          return activity.title;
        }}
      />
    </div>
  );
};

export default ActivitiesSection;
